const FETCH_PATCH_FLAG = Symbol.for('b2b-commerce-platfor.frontend.tracing.fetchPatchInstalled')
const SESSION_CORRELATION_STORAGE_KEY = 'b2b-commerce-platfor.frontend.trace.correlation_id'

type FrontendTraceInstallOptions = {
  apiBase?: string
}

type FrontendTraceTargetOptions = {
  apiBase?: string
  browserOrigin: string
}

let inMemoryCorrelationId = ''

const normalizeBasePath = (value: string) => value.replace(/\/+$/, '')

const resolveBrowserOrigin = () => {
  const locationLike = globalThis.location
  if (locationLike && typeof locationLike.origin === 'string' && locationLike.origin.trim()) {
    return locationLike.origin
  }
  return 'http://localhost'
}

const resolveTargetUrl = (input: RequestInfo | URL, browserOrigin: string) => {
  if (input instanceof URL) return input
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return new URL(input.url, browserOrigin)
  }
  return new URL(String(input), browserOrigin)
}

const isUrlWithinBase = (targetUrl: URL, baseUrl: URL) => {
  if (targetUrl.origin !== baseUrl.origin) return false

  const basePath = normalizeBasePath(baseUrl.pathname || '')
  if (!basePath || basePath === '/') return true

  return targetUrl.pathname === basePath || targetUrl.pathname.startsWith(`${basePath}/`)
}

export const shouldInjectFrontendTraceHeaders = (
  targetUrlRaw: string,
  options: FrontendTraceTargetOptions
) => {
  const browserOrigin = String(options.browserOrigin || 'http://localhost').trim() || 'http://localhost'
  const targetUrl = new URL(targetUrlRaw, browserOrigin)
  const browserBaseUrl = new URL(browserOrigin)

  if (isUrlWithinBase(targetUrl, browserBaseUrl)) {
    return true
  }

  const apiBase = String(options.apiBase ?? '').trim()
  if (!apiBase) return false

  const apiBaseUrl = new URL(apiBase, browserOrigin)
  return isUrlWithinBase(targetUrl, apiBaseUrl)
}

const randomHex = (bytes: number) => {
  const size = Math.max(1, Math.trunc(bytes))
  const buffer = new Uint8Array(size)

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(buffer)
  } else {
    for (let index = 0; index < size; index += 1) {
      buffer[index] = Math.floor(Math.random() * 256)
    }
  }

  return Array.from(buffer, (value) => value.toString(16).padStart(2, '0')).join('')
}

const buildFrontendTraceParent = () => `00-${randomHex(16)}-${randomHex(8)}-01`

const getSessionCorrelationId = () => {
  if (inMemoryCorrelationId) return inMemoryCorrelationId

  try {
    const cached = globalThis.sessionStorage?.getItem(SESSION_CORRELATION_STORAGE_KEY)
    if (cached) {
      inMemoryCorrelationId = cached
      return cached
    }
  } catch {
    // Ignore storage access failures and fall back to in-memory state.
  }

  const created = `fe-session-${randomHex(8)}`
  inMemoryCorrelationId = created

  try {
    globalThis.sessionStorage?.setItem(SESSION_CORRELATION_STORAGE_KEY, created)
  } catch {
    // Ignore storage write failures and keep the in-memory fallback.
  }

  return created
}

const cloneHeaders = (input: RequestInfo | URL, init?: RequestInit) => {
  const headers = new Headers(init?.headers)

  if (typeof Request !== 'undefined' && input instanceof Request) {
    for (const [key, value] of input.headers.entries()) {
      if (!headers.has(key)) {
        headers.set(key, value)
      }
    }
  }

  return headers
}

const buildInstrumentedInput = (input: RequestInfo | URL, headers: Headers) => {
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return new Request(input, { headers })
  }
  return input
}

export const installFrontendTracePropagation = (options: FrontendTraceInstallOptions = {}) => {
  if ((globalThis as Record<PropertyKey, unknown>)[FETCH_PATCH_FLAG]) return
  if (typeof globalThis.fetch !== 'function') return

  const browserOrigin = resolveBrowserOrigin()
  const apiBase = String(options.apiBase ?? '').trim()
  const originalFetch = globalThis.fetch.bind(globalThis)

  const instrumentedFetch: typeof globalThis.fetch = async (input, init) => {
    const targetUrl = resolveTargetUrl(input, browserOrigin)
    if (!shouldInjectFrontendTraceHeaders(targetUrl.toString(), { apiBase, browserOrigin })) {
      return originalFetch(input, init)
    }

    const headers = cloneHeaders(input, init)
    if (!headers.has('traceparent')) {
      headers.set('traceparent', buildFrontendTraceParent())
    }
    if (!headers.has('x-request-id')) {
      headers.set('x-request-id', `fe-req-${randomHex(8)}`)
    }
    if (!headers.has('x-correlation-id')) {
      headers.set('x-correlation-id', getSessionCorrelationId())
    }

    const nextInput = buildInstrumentedInput(input, headers)
    const nextInit = init ? { ...init, headers } : { headers }
    return originalFetch(nextInput, nextInit)
  }

  globalThis.fetch = instrumentedFetch
  ;(globalThis as Record<PropertyKey, unknown>)[FETCH_PATCH_FLAG] = true
}
